/* Quiz — artboard 1f, made real.

   The artboard shows one question beside a finished result panel. Here the
   panel tracks the attempt live: progress while you work, resolving into the
   score, the per-outcome breakdown and what unlocked.

   Per-outcome scoring is the point — it is what the mastery model consumes, and
   what a remediation page is generated from.

   A question marked `ungraded: true` asks about the student, not the material
   (the Orientation diagnostic's "have you programmed before?", D-027). It has no
   right answer: it is left out of the score and the breakdown, its choice is
   shown without right/wrong colour, and the chosen answer's `note` replaces
   the WHY and its `result` is added to the finished result. An ungraded quiz's
   own `result:` text replaces the default result sentence.
*/

import { useState, useMemo } from 'react';
import s from './Quiz.module.css';
import { RuleRow, Kicker, Button, CodeBlock } from '../design/primitives.jsx';
import { inline } from '../content/inline.jsx';

const cx = (...v) => v.filter(Boolean).join(' ');

const Ring = ({ score, total, ungraded }) => {
  const pct = total ? Math.round((score / total) * 100) : 0;
  return (
    <div className={s.ring}
         style={{ background: `conic-gradient(var(--accent) 0 ${pct}%, var(--ink-12) ${pct}% 100%)` }}>
      <div className={s.ringInner}>
        <span className={s.ringScore}>{score}/{total}</span>
        {!ungraded && <span className={s.ringLabel}>{pct >= 70 ? 'PASSED' : 'NOT YET'}</span>}
      </div>
    </div>
  );
};

export default function Quiz({ quiz, unlocks, onDone }) {
  const [picks, setPicks] = useState({});
  const [i, setI] = useState(0);

  const q = quiz.questions[i];
  const picked = picks[q.id] ?? null;
  const revealed = picked !== null;
  const answered = Object.keys(picks).length;
  const finished = answered === quiz.questions.length;

  const isCorrect = (qq, key) => !!qq.answers.find(a => a.key === key)?.correct;
  const graded = useMemo(() => quiz.questions.filter(qq => !qq.ungraded), [quiz.questions]);
  const total = graded.length;
  const score = useMemo(
    () => graded.filter(qq => picks[qq.id] && isCorrect(qq, picks[qq.id])).length,
    [picks, graded]);
  /* What the student said about themselves, from any ungraded question. */
  const about = quiz.questions.filter(qq => qq.ungraded && picks[qq.id])
    .map(qq => qq.answers.find(a => a.key === picks[qq.id])?.result).filter(Boolean);

  /* Per-outcome breakdown — what the mastery model consumes. */
  const skills = useMemo(() => {
    const by = new Map();
    for (const qq of graded) {
      const key = qq.skill || `Outcome ${qq.outcome ?? '—'}`;
      const rec = by.get(key) || { name: key, got: 0, total: 0 };
      rec.total += 1;
      if (picks[qq.id] && isCorrect(qq, picks[qq.id])) rec.got += 1;
      by.set(key, rec);
    }
    return [...by.values()].map(r => ({ ...r, pct: Math.round((r.got / r.total) * 100) }));
  }, [picks, graded]);

  /* "I don't know" (D-040) is offered on every graded question, by the
     component rather than per quiz. It counts as a miss, reveals the right
     answer, and shows the rule — not the note written for a wrong answer. */
  const IDK = '__idk';
  const unknown = picked === IDK;
  const right = picked && isCorrect(q, picked);
  const note = q.ungraded ? q.answers.find(a => a.key === picked)?.note : null;

  return (
    <div className={s.quiz}>
      <div className={s.main}>
        <RuleRow kicker={quiz.kicker} meta={`Q${i + 1} of ${quiz.questions.length}`} />
        {quiz.intro && <p className={s.intro}>{inline(quiz.intro)}</p>}

        <div className={s.pips}>
          {quiz.questions.map((qq, j) => {
            const p = picks[qq.id];
            return (
              <button key={qq.id} type="button"
                      className={cx(s.pip, p && (qq.ungraded ? s.done : isCorrect(qq, p) ? s.right : s.wrong), !p && j === i && s.here)}
                      aria-label={`Question ${j + 1}`}
                      onClick={() => setI(j)} />
            );
          })}
        </div>

        <h2 className={s.prompt}>{inline(q.prompt)}</h2>
        {q.code && <CodeBlock>{q.code}</CodeBlock>}

        <div className={s.answers}>
          {q.answers.map(a => {
            const chosen = picked === a.key;
            return (
              <button key={a.key} type="button" disabled={revealed}
                      onClick={() => setPicks(p => ({ ...p, [q.id]: a.key }))}
                      className={cx(s.answer,
                        q.ungraded && chosen && s.picked,
                        !q.ungraded && revealed && a.correct && s.right,
                        !q.ungraded && chosen && !a.correct && s.wrong)}>
                <span className={s.key}>{a.key}</span>
                <span className={s.answerText}>{inline(a.text)}</span>
                {revealed && !q.ungraded && a.tag && <span className={s.answerTag}>{a.tag}</span>}
              </button>
            );
          })}
          {!q.ungraded && !revealed && (
            <button type="button" className={s.idk} onClick={() => setPicks(p => ({ ...p, [q.id]: IDK }))}>
              I don't know
            </button>
          )}
          {unknown && <p className={s.idkNote}>Noted — here is the answer.</p>}
        </div>

        <div className={cx(s.why, revealed && s.open)}>
          <Kicker spacing="tight">{q.ungraded ? 'NOTED' : 'WHY'}</Kicker>
          <p>{inline(q.ungraded ? note : right || unknown ? q.explain?.right : q.explain?.wrong)}</p>
        </div>

        <div className={s.nav}>
          {i > 0 && <Button variant="secondary" onClick={() => setI(i - 1)}>← Previous</Button>}
          {i < quiz.questions.length - 1 &&
            <Button onClick={() => setI(i + 1)} disabled={!revealed}>Next question →</Button>}
        </div>
      </div>

      <aside className={s.aside}>
        <Kicker>{finished ? (quiz.ungraded ? 'WHERE YOU START' : 'TRIAL RESULT') : 'PROGRESS'}</Kicker>

        <div className={s.result}>
          <Ring score={score} total={total} ungraded={quiz.ungraded} />
          <div className={s.resultText}>
            <div className={s.resultTitle}>
              {!finished ? `${answered} of ${quiz.questions.length} answered`
                : quiz.ungraded ? `${score} of ${total} already familiar`
                : (score / total >= 0.7 ? 'Trial cleared' : 'Not cleared yet')}
            </div>
            <p className={s.resultBody}>
              {/* An ungraded diagnostic is a map, not a verdict — no pass mark,
                  no "retake". A pass/fail frame here contradicts the one thing
                  the page promises (05-module rule 15a). */}
              {!finished ? 'Answer every question to see the full breakdown.'
                : quiz.ungraded ? inline(quiz.result || 'Nothing here is graded. The outcomes below show where the course will be new to you.')
                : (score / total >= 0.7
                    ? 'Review anything below that is short of full marks, then move on.'
                    : 'Work through the outcomes below and retake — this check can be repeated.')}
            </p>
            {finished && about.map((t, k) => <p key={k} className={s.resultBody}>{inline(t)}</p>)}
          </div>
        </div>

        <div className={s.skills}>
          {skills.map(sk => (
            <div key={sk.name} className={s.skill}>
              <div className={s.skillHead}>
                <span>{sk.name}</span>
                <span className={s.skillScore}>{sk.got}/{sk.total}</span>
              </div>
              <div className={s.skillBar}>
                <span className={sk.pct < 60 ? s.low : undefined} style={{ width: `${sk.pct}%` }} />
              </div>
            </div>
          ))}
        </div>

        {finished && unlocks && (
          <div className={s.unlocked}>
            <Kicker spacing="tight">UNLOCKED</Kicker>
            <div className={s.unlockedName}>{unlocks}</div>
          </div>
        )}

        <div className={s.asideActions}>
          <Button variant="secondary" onClick={() => { setPicks({}); setI(0); }}>Reset</Button>
          <Button disabled={!finished} onClick={() => onDone?.(score)}>Travel onward</Button>
        </div>
      </aside>
    </div>
  );
}
